import { Component, Output, EventEmitter, Inject, ChangeDetectionStrategy } from '@angular/core';

import { MatFormField, MatLabel } from "@angular/material/form-field";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle
} from "@angular/material/dialog";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import * as QRCode from 'qrcode';
import { MatInput } from "@angular/material/input";
import { MFAService, TOTPInitRegistrationResponse } from "@fry/lib/auth/account/mfa/mfa.service";
import { MFAMethod, MFAMethodsEnum } from "@fry/lib/auth/account/mfa/mfa";
import { MatButton } from '@angular/material/button';


@Component({
    selector: 'app-mfa-setup',
    templateUrl: './create-mfa-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    MatFormField,
    MatInput,
    MatDialogTitle,
    MatDialogContent,
    MatLabel,
    MatButton,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule
]
})
export class CreateMfaDialogComponent {
  @Output() completed = new EventEmitter<any>();

  protected readonly MFAMethodsEnum = MFAMethodsEnum;

  credentialUsername: string = '';
  availableMethods: MFAMethod[] = [];
  isPreselectedMethod: boolean = false;
  currentStep: 'selectMethod' | 'completeRegistration' = 'selectMethod';
  selectedMethod: MFAMethodsEnum | null = null;

  errorMessage: string = '';
  mfaId: string | null = null;

  // Email OTP variables
  otpForm: FormGroup;

  // TOTP variables
  totpForm: FormGroup;
  qrCodeDataUrl: string | null = null;
  secretKey: string | null = null;


  constructor(
    private mfaService: MFAService,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: {
      credentialUsername: string,
      availableMethods: MFAMethod[],
      preSelectMethod?: MFAMethodsEnum
    }
  ) {
    this.credentialUsername = data.credentialUsername;
    this.availableMethods = data.availableMethods;
    if(data.preSelectMethod) {
      this.selectMethod(data.preSelectMethod);
      this.isPreselectedMethod = true;
    }

    this.otpForm = this.fb.group({
      code: ['', Validators.required],
    });

    this.totpForm = this.fb.group({
      code: ['', Validators.required],
    });
  }

  selectMethod(method: MFAMethodsEnum.TOTP | MFAMethodsEnum.EMAIL_OTP) {
    this.selectedMethod = method;
    this.currentStep = 'completeRegistration';
    this.initRegistration(method);
  }

  // Initiate Registration
  initRegistration(method: MFAMethodsEnum.TOTP | MFAMethodsEnum.EMAIL_OTP) {
    const observable = method === MFAMethodsEnum.EMAIL_OTP
      ? this.mfaService.initEmailOTPRegistration(this.credentialUsername)
      : this.mfaService.initTOTPRegistration(this.credentialUsername);

    observable.subscribe({
      next: (response) => {
        this.mfaId = response.mfa_id;
        if (method === MFAMethodsEnum.TOTP) {
          const totpResponse = response as TOTPInitRegistrationResponse;
          const match = totpResponse.uri.match(/secret=([^&]+)/);
          if (match) {
            this.secretKey = match[1];
          }
          QRCode.toDataURL(totpResponse.uri).then(url => this.qrCodeDataUrl = url);
        }
      },
      error: (err) => this.errorMessage = `Failed, reason: ${err}.`
    });
  }

  // Complete Registration (Unified for both TOTP and Email OTP)
  completeRegistration() {
    const code = this.selectedMethod === MFAMethodsEnum.EMAIL_OTP ? this.otpForm.value.code : this.totpForm.value.code;

    const observable = this.selectedMethod === MFAMethodsEnum.EMAIL_OTP
      ? this.mfaService.completeEmailOTPRegistration(this.credentialUsername, this.mfaId!, code)
      : this.mfaService.completeTOTPRegistration(this.credentialUsername, this.mfaId!, code);

    observable.subscribe({
      next: () => {
        this.completed.emit({ method: this.selectedMethod, status: 'success' });
      },
      error: (err) => this.errorMessage = `Failed, reason: ${err}.`
    });
  }

  // Determine if the Complete Registration button should be disabled
  isCompleteRegistrationDisabled(): boolean {
    return this.selectedMethod === MFAMethodsEnum.EMAIL_OTP
      ? !this.otpForm.valid
      : this.selectedMethod === MFAMethodsEnum.TOTP
        ? !this.totpForm.valid
        : true;
  }

  // Utility function for copying TOTP secret key
  async copyToClipboard() {
    if (this.secretKey) {
      try {
        await navigator.clipboard.writeText(this.secretKey);
      } catch(err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  }

  // Go back to the previous step
  goBack() {
    this.currentStep = 'selectMethod';
    this.selectedMethod = null;
    this.errorMessage = '';
    this.qrCodeDataUrl = null;
    this.secretKey = null;
    this.otpForm.reset();
    this.totpForm.reset();
  }

  closeDialog() {
    this.completed.emit({ method: null, status: 'cancelled' });
  }

}
